/**
 * Ablage aller Objekte und Protokolle in localStorage.
 *
 * Bewusst der einfachste offline-fähige Speicher, den es gibt — kein
 * IndexedDB, kein Sync. Reicht, solange ein Gerät die Daten allein
 * bearbeitet. Der Offline-Sync über mehrere Geräte ist ein eigenes,
 * noch ungelöstes Thema (siehe docs/HANDOFF.md) und ersetzt diese Schicht
 * später, statt sie zu erweitern.
 */
import type { Objekt, Protokoll } from './domain/types';

const OBJEKTE_SCHLUESSEL = 'uebergabe.objekte';
const PROTOKOLLE_SCHLUESSEL = 'uebergabe.protokolle';
const AKTUELLE_PROTOKOLL_ID_SCHLUESSEL = 'uebergabe.aktuelle-protokoll-id';
const SCHRITT_SCHLUESSEL = 'uebergabe.aktueller-schritt';

function ladeListe<T>(schluessel: string): T[] {
	const roh = localStorage.getItem(schluessel);
	if (!roh) return [];
	try {
		const wert = JSON.parse(roh);
		return Array.isArray(wert) ? (wert as T[]) : [];
	} catch {
		return [];
	}
}

export function ladeObjekte(): Objekt[] {
	return ladeListe<Objekt>(OBJEKTE_SCHLUESSEL);
}

export function speichereObjekte(objekte: Objekt[]): void {
	localStorage.setItem(OBJEKTE_SCHLUESSEL, JSON.stringify(objekte));
}

export function ladeProtokolle(): Protokoll[] {
	return ladeListe<Protokoll>(PROTOKOLLE_SCHLUESSEL);
}

export function speichereProtokolle(protokolle: Protokoll[]): void {
	localStorage.setItem(PROTOKOLLE_SCHLUESSEL, JSON.stringify(protokolle));
}

/** Ein einzelnes Protokoll in der Liste aktualisieren (oder anhängen, falls neu). */
export function speichereProtokoll(protokoll: Protokoll): Protokoll[] {
	const alle = ladeProtokolle();
	const index = alle.findIndex((p) => p.id === protokoll.id);
	const neu = index >= 0 ? alle.map((p, i) => (i === index ? protokoll : p)) : [...alle, protokoll];
	speichereProtokolle(neu);
	return neu;
}

/** Welches Protokoll gerade bearbeitet wird — null heißt: Übersicht anzeigen. */
export function ladeAktuelleProtokollId(): string | null {
	return localStorage.getItem(AKTUELLE_PROTOKOLL_ID_SCHLUESSEL);
}

export function speichereAktuelleProtokollId(id: string | null): void {
	if (id) localStorage.setItem(AKTUELLE_PROTOKOLL_ID_SCHLUESSEL, id);
	else localStorage.removeItem(AKTUELLE_PROTOKOLL_ID_SCHLUESSEL);
}

/**
 * An welchem Schritt der Nutzer im aktuellen Protokoll zuletzt war — wird
 * zusammen gespeichert, damit ein Absturz oder ein versehentliches
 * Schließen des Browsers nicht mitten im Rundgang zurück auf Los wirft.
 */
export function ladeSchritt(): string | null {
	return localStorage.getItem(SCHRITT_SCHLUESSEL);
}

export function speichereSchritt(schritt: string): void {
	localStorage.setItem(SCHRITT_SCHLUESSEL, schritt);
}

/**
 * Zurück zur Übersicht — das aktuelle Protokoll bleibt inklusive seines
 * Objekts vollständig in der Historie erhalten, nur der "was gerade
 * bearbeitet wird"-Zeiger wird geleert. Nichts wird gelöscht.
 */
export function beendeAktuellesProtokoll(): void {
	speichereAktuelleProtokollId(null);
	localStorage.removeItem(SCHRITT_SCHLUESSEL);
}

/** Alles exportieren — für Backup/Übertragung auf ein neues Gerät (siehe backup.ts). */
export function ladeAllesFuerExport(): { objekte: Objekt[]; protokolle: Protokoll[] } {
	return { objekte: ladeObjekte(), protokolle: ladeProtokolle() };
}
